import type { JSX } from 'react'
import { InputSwitch } from 'primereact/inputswitch'
import {
  RECORDING_TYPES,
  TenantRecordingConfigSchema,
  type TenantRecordingConfigInput,
} from '@kc3/contracts'

export interface ProvisionStepRecordingProps {
  value: TenantRecordingConfigInput
  onChange: (v: TenantRecordingConfigInput) => void
  readOnly?: boolean
}

const TYPE_LABEL: Record<(typeof RECORDING_TYPES)[number], string> = {
  all: 'All calls',
  inbound: 'Inbound calls',
  outbound: 'Outbound calls',
}

const RECORDING_FIELDS = [
  'recordingEnabled',
  'announcementEnabled',
  'announcementMessage',
  'recordingType',
  'retentionDays',
  'allowAgentPause',
] as const

/** True when the 5 core fields differ (extra keys like updatedAt are ignored). */
export function recordingChanged(
  current: TenantRecordingConfigInput,
  original: TenantRecordingConfigInput,
): boolean {
  return RECORDING_FIELDS.some((k) => (current[k] ?? null) !== (original[k] ?? null))
}

/** Field → message for the first issue on each field; {} when valid. */
export function validateRecording(v: TenantRecordingConfigInput): Record<string, string> {
  const r = TenantRecordingConfigSchema.safeParse(v)
  if (r.success) return {}
  const errors: Record<string, string> = {}
  for (const issue of r.error.issues) {
    const key = String(issue.path[0] ?? '')
    if (key && !errors[key]) errors[key] = issue.message
  }
  return errors
}

const inputCls =
  'w-full border border-gray-200 rounded-sm px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400'
const labelCls = 'text-sm font-semibold text-gray-700'

export function ProvisionStepRecording({
  value,
  onChange,
  readOnly = false,
}: ProvisionStepRecordingProps): JSX.Element {
  const errors = validateRecording(value)
  const set = (patch: Partial<TenantRecordingConfigInput>): void => onChange({ ...value, ...patch })
  const off = readOnly || !value.recordingEnabled
  const announcementOff = off || !value.announcementEnabled

  const err = (k: string): JSX.Element | null =>
    errors[k] ? <p className="text-xs text-red-500">{errors[k]}</p> : null

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-sm border border-gray-100 p-6 flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-gray-900">Recording Configuration</p>
          <p className="text-xs text-gray-400">Enable call recording for this tenant</p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="rec-enabled" className={labelCls}>Recording Enabled</label>
          <InputSwitch
            inputId="rec-enabled"
            checked={value.recordingEnabled}
            onChange={(e) => set({ recordingEnabled: e.value ?? false })}
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="bg-white rounded-sm border border-gray-100 p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rec-type" className={labelCls}>Recording Type</label>
          <select id="rec-type" className={inputCls} disabled={off} value={value.recordingType}
            onChange={(e) => set({ recordingType: e.target.value as TenantRecordingConfigInput['recordingType'] })}>
            {RECORDING_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rec-retention" className={labelCls}>Retention (days)</label>
          <input id="rec-retention" type="number" min={1} max={3650} className={inputCls} disabled={off}
            value={value.retentionDays ?? ''}
            onChange={(e) => set({ retentionDays: e.target.value === '' ? null : Number(e.target.value) })} />
          {err('retentionDays')}
        </div>
      </div>

      <div className="bg-white rounded-sm border border-gray-100 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <InputSwitch inputId="rec-announce" checked={value.announcementEnabled}
            onChange={(e) => set({ announcementEnabled: e.value ?? false })} disabled={off} />
          <label htmlFor="rec-announce" className={labelCls}>Recording Announcement</label>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rec-message" className={labelCls}>Announcement Message</label>
          <textarea id="rec-message" rows={3} maxLength={1000} className={inputCls} disabled={announcementOff}
            placeholder="This call may be recorded for quality and training purposes."
            value={value.announcementMessage ?? ''}
            onChange={(e) => set({ announcementMessage: e.target.value || null })} />
          {err('announcementMessage')}
        </div>
      </div>

      <div className="bg-white rounded-sm border border-gray-100 p-6 flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-gray-900">Allow Agents to Pause/Resume Call Recording</p>
          <p className="text-xs text-gray-400">When enabled, agents can temporarily pause and resume call recording during an active call.</p>
        </div>
        <div className="flex items-center gap-2">
          <InputSwitch
            inputId="rec-allow-agent-pause"
            checked={value.allowAgentPause ?? false}
            onChange={(e) => set({ allowAgentPause: e.value ?? false })}
            disabled={readOnly}
          />
        </div>
      </div>
    </div>
  )
}
